import React, { useEffect, useRef, useState } from 'react';
import { ApiError, type AdminApiClient, type JsonRecord } from './api-client';
import { ActionButton, ConfirmDialog, Notice } from './admin-feedback';
import {
  BUSINESS_PERMISSION_GROUPS,
  BUSINESS_STATES,
  FIELD_CHOICES,
  SECURITY_TABS,
  cloneRole,
  commonFieldsForGrantedFunctions,
  canonicalFieldRules,
  businessMenuPreview,
  organizationParentOptions,
  organizationEffectiveActive,
  organizationTree,
  permissionActionName,
  presentPermission,
  readBusinessWorkspace,
  replaceFieldRule,
  replaceStateRule,
  roleDirty,
  securityRecord,
  securityText,
  tabForSecurityPage,
  updateBusinessPermission,
  updateBusinessGroup,
  updateScopeCity,
  type BusinessSecurityWorkspace as WorkspaceData,
  type FieldRule,
  type SecurityRole,
  type SecurityOrganization,
  type SecurityTab,
  type SecurityUser,
  type StateRule,
} from './business-security-model';
import './business-security-workspace.css';
import { EmployeeWorkspace } from './employee-workspace';
import { PermissionScopeEditor } from './permission-scope-editor';

function errorMessage(error: unknown, fallback: string, secrets: readonly string[] = []): string {
  if (error instanceof ApiError && error.status === 403)
    return '当前账号没有执行此操作的权限，请联系平台管理员。';
  if (error instanceof ApiError && error.status === 409)
    return '资料已被其他管理员修改，请重新加载后再操作。';
  return error instanceof Error &&
    /[\u3400-\u9fff]/u.test(error.message) &&
    !secrets.some((secret) => secret && error.message.includes(secret))
    ? error.message
    : fallback;
}

async function boundedRequest<T>(
  api: AdminApiClient,
  path: string,
  init: RequestInit,
  parent?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  let cancel: ((reason: unknown) => void) | undefined;
  const cancelled = new Promise<never>((_, reject) => {
    cancel = reject;
  });
  const abort = () => {
    controller.abort();
    cancel?.(new Error('Request cancelled'));
  };
  if (parent?.aborted) abort();
  parent?.addEventListener('abort', abort, { once: true });
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(new Error('Request deadline reached'));
    }, 15_000);
  });
  try {
    return await Promise.race([
      api.request<T>(path, { ...init, signal: controller.signal, cache: 'no-store' }),
      cancelled,
      deadline,
    ]);
  } catch (error) {
    if (timedOut) throw new Error('请求超时，请重新加载后再试。', { cause: error });
    throw error;
  } finally {
    if (timeout) clearTimeout(timeout);
    parent?.removeEventListener('abort', abort);
  }
}
const selectedEffect = (role: SecurityRole | undefined, permissionId: string) =>
  role?.permissions.find((rule) => rule.permissionId === permissionId)?.effect ?? null;
const badge = (active: boolean, yes = '启用', no = '停用') => (
  <span className={`bsw-badge ${active ? 'is-good' : 'is-muted'}`}>{active ? yes : no}</span>
);
const fieldLabel = (name: string) =>
  FIELD_CHOICES.find((field) => field.name === name)?.label ?? '其他已配置业务信息';
const stateLabel = (state: string) =>
  BUSINESS_STATES.find((item) => item.value === state)?.name ?? '其他已配置业务阶段';

export function BusinessSecurityWorkspace(props: {
  api: AdminApiClient;
  targetAppId: string;
  pageId: string;
  initialTab?: SecurityTab;
  staffMode?: 'new' | 'edit';
  staffUserId?: string;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  if ((props.initialTab ?? tabForSecurityPage(props.pageId)) === 'staff')
    return (
      <EmployeeWorkspace
        api={props.api}
        staffMode={props.staffMode}
        staffUserId={props.staffUserId}
        onDirtyChange={props.onDirtyChange}
      />
    );
  return <BusinessSecurityAuthorizationWorkspace {...props} />;
}

function BusinessSecurityAuthorizationWorkspace({
  api,
  pageId,
  initialTab,
  onDirtyChange,
}: {
  api: AdminApiClient;
  targetAppId: string;
  pageId: string;
  initialTab?: SecurityTab;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [tab, setTab] = useState<SecurityTab>(initialTab ?? tabForSecurityPage(pageId));
  const [data, setData] = useState<WorkspaceData>();
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false),
    [feedback, setFeedback] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [savedRole, setSavedRole] = useState<SecurityRole>(),
    [roleDraft, setRoleDraft] = useState<SecurityRole>();
  const [savedFields, setSavedFields] = useState<FieldRule[]>([]),
    [fieldDraft, setFieldDraft] = useState<FieldRule[]>([]);
  const [savedStates, setSavedStates] = useState<StateRule[]>([]),
    [stateDraft, setStateDraft] = useState<StateRule[]>([]);
  const [permissionSearch, setPermissionSearch] = useState(''),
    [employeeSearch, setEmployeeSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [newRoleName, setNewRoleName] = useState(''),
    [newRoleDescription, setNewRoleDescription] = useState('');
  const [newAccount, setNewAccount] = useState(''),
    [newEmployeeName, setNewEmployeeName] = useState(''),
    [newEmployeeOrg, setNewEmployeeOrg] = useState('');
  const [newEmployeeError, setNewEmployeeError] = useState('');
  const [newOrganizationName, setNewOrganizationName] = useState(''),
    [newOrganizationParent, setNewOrganizationParent] = useState('');
  const [editingOrganization, setEditingOrganization] = useState<SecurityOrganization>();
  const [organizationDraft, setOrganizationDraft] = useState<SecurityOrganization>();
  const [editingUser, setEditingUser] = useState<SecurityUser>(),
    [employeeRoles, setEmployeeRoles] = useState<string[]>([]);
  const [enrollingUser, setEnrollingUser] = useState<SecurityUser>();
  const [resettingUser, setResettingUser] = useState<SecurityUser>();
  const [resetPassword, setResetPassword] = useState(''),
    [resetConfirmation, setResetConfirmation] = useState(''),
    [resetError, setResetError] = useState('');
  const [initialPassword, setInitialPassword] = useState(''),
    [confirmedPassword, setConfirmedPassword] = useState(''),
    [enrollmentError, setEnrollmentError] = useState('');
  const [movingUser, setMovingUser] = useState<SecurityUser>(),
    [movingOrgId, setMovingOrgId] = useState('');
  const [movePreview, setMovePreview] = useState<JsonRecord>();
  const [previewBusy, setPreviewBusy] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<() => void>();
  const [confirmation, setConfirmation] = useState<{
    title: string;
    description: React.ReactNode;
    action: () => void;
  }>();
  const [orderOrganizations, setOrderOrganizations] = useState<Record<string, string>>({});
  const generation = useRef(0),
    running = useRef(false),
    operation = useRef<AbortController>();
  const readGeneration = useRef(0);
  const selectedRole = useRef('');
  const dataRef = useRef<WorkspaceData>();
  const dirty =
    roleDirty(savedRole, roleDraft) ||
    JSON.stringify(fieldDraft) !== JSON.stringify(savedFields) ||
    JSON.stringify(stateDraft) !== JSON.stringify(savedStates) ||
    Boolean(
      newRoleName ||
      newRoleDescription ||
      newAccount ||
      newEmployeeName ||
      newOrganizationName ||
      editingOrganization ||
      editingUser ||
      enrollingUser ||
      resettingUser ||
      movingUser,
    );
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const base = '/api/admin/m8/security';
  const workspaceBase = `${base}/workspace`;
  const canManage = data?.currentUser.canManage === true;
  const managementBlocked = busy || loading || Boolean(loadError) || !canManage;
  const roleTabs = ['roles', 'menus', 'scope', 'fields', 'sensitive'].includes(tab);
  const selfRole = Boolean(
    data?.users.find((user) => user.id === data.currentUser.id)?.roleIds.includes(selectedRoleId),
  );
  const blocked = managementBlocked || (roleTabs && selfRole);

  function adoptRole(next: WorkspaceData, id: string) {
    const role = next.roles.find((item) => item.id === id) ?? next.roles[0];
    selectedRole.current = role?.id ?? '';
    setSelectedRoleId(selectedRole.current);
    setSavedRole(role ? cloneRole(role) : undefined);
    setRoleDraft(role ? cloneRole(role) : undefined);
    const fields = next.fieldPolicies
      .filter((rule) => rule.roleId === role?.id)
      .map(({ permissionId, fieldName, classification, access }) => ({
        permissionId,
        fieldName,
        classification,
        access,
      }));
    setSavedFields(fields);
    setFieldDraft(fields.map((rule) => ({ ...rule })));
    const states = next.statePolicies
      .filter((rule) => rule.roleId === role?.id)
      .map(({ permissionId, state, effect }) => ({ permissionId, state, effect }));
    setSavedStates(states);
    setStateDraft(states.map((rule) => ({ ...rule })));
  }
  async function read(signal: AbortSignal, id: number) {
    const readId = ++readGeneration.current;
    setLoading(true);
    setLoadError('');
    try {
      const next = readBusinessWorkspace(await boundedRequest(api, workspaceBase, {}, signal));
      if (signal.aborted || generation.current !== id || readGeneration.current !== readId) return;
      dataRef.current = next;
      setData(next);
      adoptRole(next, selectedRole.current);
    } catch (error) {
      if (!signal.aborted && generation.current === id && readGeneration.current === readId) {
        setLoadError(errorMessage(error, '授权资料读取失败，请重新加载后再配置。'));
        throw error;
      }
    } finally {
      if (!signal.aborted && generation.current === id && readGeneration.current === readId)
        setLoading(false);
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    const id = ++generation.current;
    operation.current?.abort();
    running.current = false;
    setBusy(false);
    dataRef.current = undefined;
    setData(undefined);
    setSavedRole(undefined);
    setRoleDraft(undefined);
    setSavedFields([]);
    setFieldDraft([]);
    setSavedStates([]);
    setStateDraft([]);
    setEditingUser(undefined);
    setEnrollingUser(undefined);
    setResettingUser(undefined);
    setResetPassword('');
    setResetConfirmation('');
    setResetError('');
    setInitialPassword('');
    setConfirmedPassword('');
    setEnrollmentError('');
    setMovingUser(undefined);
    setMovePreview(undefined);
    setPreviewBusy(false);
    setEditingOrganization(undefined);
    setOrganizationDraft(undefined);
    setConfirmation(undefined);
    setPendingNavigation(undefined);
    setOrderOrganizations({});
    setNewRoleName('');
    setNewRoleDescription('');
    setNewAccount('');
    setNewEmployeeName('');
    setNewEmployeeOrg('');
    setNewEmployeeError('');
    setNewOrganizationName('');
    setNewOrganizationParent('');
    setMovingOrgId('');
    setDepartmentFilter('');
    setFeedback('');
    selectedRole.current = '';
    setSelectedRoleId('');
    void read(controller.signal, id).catch(() => undefined);
    return () => {
      controller.abort();
      operation.current?.abort();
    };
  }, [api]);
  useEffect(() => {
    setTab(initialTab ?? tabForSecurityPage(pageId));
    setEnrollingUser(undefined);
    setResettingUser(undefined);
    setResetPassword('');
    setResetConfirmation('');
    setResetError('');
    setInitialPassword('');
    setConfirmedPassword('');
    setEnrollmentError('');
  }, [pageId, initialTab]);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const click = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>('a[href]');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || link.target === '_blank')
        return;
      const target = new URL(link.href, window.location.href);
      if (target.href === window.location.href || target.origin !== window.location.origin) return;
      event.preventDefault();
      event.stopPropagation();
      if (busy || loading || previewBusy || running.current) {
        setFeedback('正在确认授权资料，请等待当前操作完成后再离开。');
        return;
      }
      setPendingNavigation(() => () => {
        window.location.assign(target.href);
      });
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', click, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      document.removeEventListener('click', click, true);
    };
  }, [dirty, busy, loading, previewBusy]);
  function navigate(action: () => void) {
    if (busy || previewBusy) return;
    if (dirtyRef.current) setPendingNavigation(() => action);
    else action();
  }
  function switchTab(next: SecurityTab) {
    if (busy || previewBusy) return;
    if (next === 'roles' || next === 'organizations') {
      navigate(() => {
        setTab(next);
        setFeedback('');
        const path = next === 'roles' ? '/admin/security/roles' : '/admin/security/organizations';
        if (window.location.pathname !== path) {
          window.history.pushState({}, '', path);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      });
      return;
    }
    if (next === 'staff') {
      navigate(() => {
        window.history.pushState({}, '', '/admin/security/users');
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
      return;
    }
    const connected = ['roles', 'menus', 'scope'];
    const onlyRoleDraft =
      !newRoleName &&
      !newRoleDescription &&
      !newAccount &&
      !newEmployeeName &&
      !newOrganizationName &&
      !editingUser &&
      !enrollingUser &&
      !resettingUser &&
      !movingUser &&
      !editingOrganization &&
      JSON.stringify(fieldDraft) === JSON.stringify(savedFields) &&
      JSON.stringify(stateDraft) === JSON.stringify(savedStates);
    if (connected.includes(tab) && connected.includes(next) && onlyRoleDraft) {
      setTab(next);
      setFeedback('');
    } else
      navigate(() => {
        setTab(next);
        setFeedback('');
      });
  }
  async function reload() {
    if (running.current) return;
    const controller = new AbortController();
    operation.current = controller;
    try {
      await read(controller.signal, generation.current);
    } catch {
      /* Explicit retry remains available. */
    } finally {
      if (operation.current === controller) operation.current = undefined;
    }
  }
  async function command(
    label: string,
    path: string,
    method: 'POST' | 'PUT',
    body: unknown,
    onDone?: (receipt: unknown) => void | boolean,
  ) {
    if (running.current || managementBlocked || !dataRef.current) return;
    running.current = true;
    setBusy(true);
    setFeedback('');
    const controller = new AbortController();
    operation.current = controller;
    const id = generation.current;
    let committed = false;
    try {
      const receipt = await boundedRequest(
        api,
        path,
        { method, body: JSON.stringify(body) },
        controller.signal,
      );
      committed = true;
      if (controller.signal.aborted || generation.current !== id) return;
      await read(controller.signal, id);
      if (controller.signal.aborted || generation.current !== id) return;
      if (onDone?.(receipt) !== false) setFeedback(`${label}已保存，并已重新读取确认。`);
    } catch (error) {
      if (!controller.signal.aborted && generation.current === id) {
        if (!committed && (!(error instanceof ApiError) || error.status >= 500))
          setLoadError('提交结果尚未确认，请重新读取确认后再修改，避免重复提交。');
        setFeedback(
          committed
            ? '已提交，但重新读取未完成。请重新加载确认后再修改。'
            : errorMessage(
                error,
                `${label}保存失败，请重试。`,
                ['password', 'currentPassword', 'newPassword']
                  .map((key) => securityText(securityRecord(body), key))
                  .filter(Boolean),
              ),
        );
      }
    } finally {
      if (operation.current === controller) operation.current = undefined;
      if (generation.current === id) {
        running.current = false;
        setBusy(false);
      }
    }
  }
  function switchRole(id: string) {
    if (busy || loading || loadError || previewBusy) return;
    navigate(() => {
      if (data) {
        adoptRole(data, id);
        setFeedback('');
      }
    });
  }
  function dismissEnrollment() {
    setEnrollingUser(undefined);
    setInitialPassword('');
    setConfirmedPassword('');
    setEnrollmentError('');
  }
  function clearResetPasswords() {
    setResetPassword('');
    setResetConfirmation('');
    setResetError('');
  }
  function dismissPasswordReset() {
    setResettingUser(undefined);
    clearResetPasswords();
  }
  function passwordResetProblem(): string {
    if (resetPassword.length < 12 || resetPassword.length > 128)
      return '新密码需要 12–128 个字符。';
    if (
      !/[A-Z]/.test(resetPassword) ||
      !/[a-z]/.test(resetPassword) ||
      !/[0-9]/.test(resetPassword)
    )
      return '新密码必须同时包含英文大写、小写和数字。';
    return resetPassword !== resetConfirmation ? '两次输入的新密码不一致。' : '';
  }
  function askPasswordReset() {
    if (managementBlocked || !resettingUser) return;
    const issue = passwordResetProblem();
    if (issue) {
      setResetError(issue);
      return;
    }
    setResetError('');
    setConfirmation({
      title: '确认重置这个员工的登录密码？',
      description: `重置“${resettingUser.displayName}”的密码会立即注销该员工全部旧登录会话。其岗位角色和数据范围保持不变；请通过安全渠道通知员工使用新密码登录。`,
      action: () => {
        setConfirmation(undefined);
        void performPasswordReset();
      },
    });
  }
  async function performPasswordReset() {
    if (managementBlocked || !resettingUser) return;
    const actual = dataRef.current?.users.find((user) => user.id === resettingUser.id);
    if (
      !actual ||
      !actual.credentialReady ||
      actual.status !== 'ACTIVE' ||
      actual.id === data?.currentUser.id
    )
      return;
    const issue = passwordResetProblem();
    if (issue) {
      setResetError(issue);
      return;
    }
    const context = generation.current;
    try {
      await command(
        '员工密码重置',
        `${workspaceBase}/users/${encodeURIComponent(actual.id)}/password-reset`,
        'POST',
        { expectedVersion: actual.version, password: resetPassword },
        (value) => {
          const receipt = securityRecord(value),
            confirmed = dataRef.current?.users.find((user) => user.id === actual.id);
          const version = Number(receipt.version),
            revoked = Number(receipt.sessionsRevokedCount);
          if (
            receipt.id !== actual.id ||
            receipt.reauthenticationRequired !== true ||
            !Number.isInteger(version) ||
            version <= actual.version ||
            !Number.isInteger(revoked) ||
            revoked < 0 ||
            !confirmed?.credentialReady ||
            confirmed.version < version
          ) {
            setLoadError('密码重置结果尚未确认，请重新读取后再操作，勿重复提交。');
            setFeedback('重置已提交，但尚未读回完整确认结果。');
            return false;
          }
          dismissPasswordReset();
          setFeedback(
            `员工密码重置已保存，并已读回确认；已注销 ${revoked} 个旧会话，原业务授权不变。`,
          );
          return false;
        },
      );
    } finally {
      if (generation.current === context) clearResetPasswords();
    }
  }
  async function enrollLogin() {
    if (managementBlocked || !enrollingUser) return;
    const actual = dataRef.current?.users.find((user) => user.id === enrollingUser.id);
    if (
      !actual ||
      actual.credentialReady ||
      actual.status !== 'ACTIVE' ||
      actual.id === data?.currentUser.id
    )
      return;
    const passwordError =
      initialPassword.length < 12 || initialPassword.length > 128
        ? '初始密码需要 12–128 个字符。'
        : !/[A-Z]/.test(initialPassword) ||
            !/[a-z]/.test(initialPassword) ||
            !/[0-9]/.test(initialPassword)
          ? '初始密码必须同时包含英文大写字母、小写字母和数字。'
          : initialPassword !== confirmedPassword
            ? '两次输入的密码不一致，请重新确认。'
            : '';
    if (passwordError) {
      setEnrollmentError(passwordError);
      return;
    }
    setEnrollmentError('');
    const context = generation.current;
    try {
      await command(
        '员工密码登录',
        `${workspaceBase}/users/${encodeURIComponent(actual.id)}/login-enrollment`,
        'POST',
        {
          expectedVersion: actual.version,
          password: initialPassword,
        },
        () => {
          const confirmed = dataRef.current?.users.find((user) => user.id === actual.id);
          if (!confirmed?.credentialReady) {
            setLoadError('登录开通结果尚未确认，请重新读取后再操作。');
            setFeedback('开通已提交，但尚未读回已开通状态，请重新读取确认。');
            return false;
          }
          dismissEnrollment();
        },
      );
    } finally {
      if (generation.current === context) {
        setInitialPassword('');
        setConfirmedPassword('');
      }
    }
  }
  function changeRole(update: (current: SecurityRole) => SecurityRole) {
    if (blocked) return;
    if (!roleDraft) return;
    try {
      setRoleDraft(update(roleDraft));
    } catch (error) {
      setFeedback(errorMessage(error, '此授权组合不能使用，请检查所需的查看权限。'));
    }
  }
  async function saveRole() {
    if (
      blocked ||
      !roleDraft ||
      !savedRole ||
      !(roleDirty(savedRole, roleDraft) || fieldsChanged || statesChanged)
    )
      return;
    const desired = cloneRole(roleDraft),
      expectedVersion = savedRole.version;
    desired.description = desired.description.trim();
    const fields = canonicalFieldRules(fieldDraft),
      states = stateDraft.map((rule) => ({ ...rule }));
    await command(
      '角色授权',
      `${workspaceBase}/roles/${encodeURIComponent(roleDraft.id)}`,
      'PUT',
      {
        expectedVersion: savedRole.version,
        name: roleDraft.name.trim(),
        description: desired.description,
        permissions: roleDraft.permissions,
        businessScopeGroups: roleDraft.businessScopeGroups,
        ...(tab === 'roles' ? { fieldPolicies: fields, statePolicies: states } : {}),
      },
      () => {
        const actual = dataRef.current?.roles.find((role) => role.id === desired.id);
        const normalized = (rules: readonly unknown[]) =>
          JSON.stringify(rules.map((rule) => JSON.stringify(rule)).sort());
        const actualFields = canonicalFieldRules(
          dataRef.current?.fieldPolicies
            .filter((rule) => rule.roleId === desired.id)
            .map(({ permissionId, fieldName, classification, access }) => ({
              permissionId,
              fieldName,
              classification,
              access,
            })) ?? [],
        );
        const actualStates =
          dataRef.current?.statePolicies
            .filter((rule) => rule.roleId === desired.id)
            .map(({ permissionId, state, effect }) => ({ permissionId, state, effect })) ?? [];
        if (
          !actual ||
          actual.version <= expectedVersion ||
          actual.name !== desired.name.trim() ||
          actual.description !== desired.description ||
          normalized(actual.permissions) !== normalized(desired.permissions) ||
          JSON.stringify(actual.businessScopeGroups) !==
            JSON.stringify(desired.businessScopeGroups) ||
          (tab === 'roles' &&
            (normalized(actualFields) !== normalized(fields) ||
              normalized(actualStates) !== normalized(states)))
        ) {
          setLoadError('角色授权尚未完整读回确认，请重新加载后再操作。');
          setFeedback('提交结果尚未确认，未显示为保存成功。');
          return false;
        }
        return true;
      },
    );
  }
  const organizationNames = new Map(data?.organizations.map((item) => [item.id, item.name]));
  const permissionRows = data?.permissions ?? [];
  const permissionLabel = (permissionId: string) => {
    const permission = permissionRows.find((item) => item.id === permissionId);
    return permission ? presentPermission(permission).title : '原有重要操作';
  };
  const visiblePermissions = permissionRows.filter((permission, index) =>
    presentPermission(permission, index).title.includes(permissionSearch.trim()),
  );
  const tree = organizationTree(data?.organizations ?? []);
  const availableParents = editingOrganization
    ? organizationParentOptions(data?.organizations ?? [], editingOrganization.id)
    : [];
  const invalidDraftParent = Boolean(
    organizationDraft?.parentId &&
    !availableParents.some((item) => item.id === organizationDraft.parentId),
  );
  const roleChanged = roleDirty(savedRole, roleDraft);
  const fieldsChanged = JSON.stringify(fieldDraft) !== JSON.stringify(savedFields);
  const statesChanged = JSON.stringify(stateDraft) !== JSON.stringify(savedStates);
  const reason = loadError
    ? '资料未成功确认，暂不能修改。'
    : loading
      ? '正在读取资料，请稍候。'
      : roleTabs && selfRole
        ? '当前账号所属岗位不能由本人修改，请选择其他岗位或由另一位管理员处理。'
        : !canManage
          ? '当前账号只有查看权限。'
          : '';

  const organizationOptions = (placeholder = '请选择所属组织') => (
    <>
      <option value="">{placeholder}</option>
      {tree
        .filter(({ organization }) =>
          organizationEffectiveActive(data?.organizations ?? [], organization.id),
        )
        .map(({ organization, depth }) => (
          <option
            key={organization.id}
            value={organization.id}
          >{`${'　'.repeat(depth)}${organization.name}`}</option>
        ))}
    </>
  );
  const saveBar = (changed: boolean, save: () => void) => (
    <div className="bsw-savebar">
      <span>
        {changed ? '有未保存的修改' : roleDraft ? '当前配置已保存' : '请选择一个岗位角色'}
        {reason && ` · ${reason}`}
      </span>
      <div>
        <button
          type="button"
          disabled={blocked || !changed}
          onClick={() =>
            setConfirmation({
              title: '放弃当前修改？',
              description: '恢复为服务端最后一次已确认的设置。',
              action: () => {
                if (data) adoptRole(data, selectedRoleId);
                setConfirmation(undefined);
              },
            })
          }
        >
          恢复已保存设置
        </button>
        <ActionButton
          type="button"
          className="bsw-primary"
          pending={busy}
          disabled={blocked || !changed || !roleDraft}
          onClick={save}
        >
          保存设置
        </ActionButton>
      </div>
    </div>
  );

  return (
    <section className="bsw-workspace" aria-label="权限与角色业务工作台">
      <header className="bsw-heading">
        <div>
          <h1>{tab === 'organizations' ? '组织架构' : '角色管理'}</h1>
          <p>明确员工职责、可操作功能和业务数据范围。</p>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            navigate(() => {
              void reload();
            })
          }
        >
          重新加载
        </button>
      </header>
      <nav className="bsw-tabs" aria-label="权限管理内容">
        {SECURITY_TABS.map((item) => (
          <button
            type="button"
            key={item.id}
            aria-current={tab === item.id ? 'page' : undefined}
            disabled={busy || previewBusy}
            onClick={() => switchTab(item.id)}
          >
            {item.name}
          </button>
        ))}
      </nav>
      {loading && <Notice>正在读取授权资料，读取完成后可继续配置。</Notice>}
      {loadError && (
        <Notice tone="error">
          {loadError}{' '}
          <button type="button" disabled={busy} onClick={() => void reload()}>
            重试读取
          </button>
        </Notice>
      )}
      {feedback && (
        <Notice tone={feedback.includes('已保存') ? 'success' : 'warning'}>{feedback}</Notice>
      )}
      {data && !canManage && <Notice>当前账号可查看授权配置，修改操作需要平台管理员权限。</Notice>}
      {data && (
        <>
          {roleTabs && (
            <div className="bsw-role-selector">
              <label>
                正在配置的岗位角色
                <select
                  aria-label="选择岗位角色"
                  value={selectedRoleId}
                  disabled={busy || loading || Boolean(loadError) || previewBusy}
                  onChange={(event) => switchRole(event.target.value)}
                >
                  {data.roles.length === 0 && <option value="">暂无岗位角色</option>}
                  {data.roles.map((role) => (
                    <option value={role.id} key={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </label>
              <span>改动只影响所选角色；未编辑的授权保持原样。</span>
            </div>
          )}
          {roleTabs && selfRole && (
            <Notice tone="warning">
              当前账号正在使用这个岗位角色，不能自行修改自己的授权。请选择其他岗位，或由另一位平台管理员处理。
            </Notice>
          )}
          {tab === 'staff' && (
            <>
              <section className="bsw-card">
                <div className="bsw-card-heading">
                  <div>
                    <h2>新增员工账号</h2>
                    <p>选择员工归属与岗位，账号开通状态以实际登录凭据为准。</p>
                  </div>
                </div>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!newAccount.trim() || !newEmployeeName.trim()) return;
                    if (!/^[a-zA-Z0-9][a-zA-Z0-9._-]{2,99}$/.test(newAccount.trim())) {
                      setNewEmployeeError(
                        '登录账号需为 3–100 个字符，以英文字母或数字开头，只可使用英文字母、数字、点、下划线和短横线。',
                      );
                      return;
                    }
                    setNewEmployeeError('');
                    void command(
                      '员工账号',
                      `${workspaceBase}/users`,
                      'POST',
                      {
                        username: newAccount.trim(),
                        displayName: newEmployeeName.trim(),
                        ...(newEmployeeOrg ? { organizationId: newEmployeeOrg } : {}),
                      },
                      () => {
                        setNewAccount('');
                        setNewEmployeeName('');
                        setNewEmployeeOrg('');
                        setNewEmployeeError('');
                      },
                    );
                  }}
                >
                  <div className="bsw-form">
                    <label>
                      员工姓名
                      <input
                        aria-label="员工姓名"
                        value={newEmployeeName}
                        disabled={blocked}
                        onChange={(event) => setNewEmployeeName(event.target.value)}
                        maxLength={150}
                      />
                    </label>
                    <label>
                      登录账号
                      <input
                        aria-label="登录账号"
                        value={newAccount}
                        disabled={blocked}
                        onChange={(event) => {
                          setNewAccount(event.target.value);
                          setNewEmployeeError('');
                        }}
                        autoComplete="off"
                        maxLength={100}
                      />
                      <small>3–100 字符，字母或数字开头，可包含 . _ -</small>
                    </label>
                    <label>
                      所属组织
                      <select
                        aria-label="新员工所属组织"
                        value={newEmployeeOrg}
                        disabled={blocked}
                        onChange={(event) => setNewEmployeeOrg(event.target.value)}
                      >
                        {organizationOptions('暂不分配组织')}
                      </select>
                    </label>
                  </div>
                  {newEmployeeError && <Notice tone="error">{newEmployeeError}</Notice>}
                  <div className="bsw-inline-actions">
                    <ActionButton
                      type="submit"
                      className="bsw-primary"
                      pending={busy}
                      disabled={blocked || !newAccount.trim() || !newEmployeeName.trim()}
                    >
                      创建员工
                    </ActionButton>
                    <span>
                      新账号不会自动获得权限；未配置密码的账号显示“待开通”。处理部门订单和客户数据还须分配所属组织。
                    </span>
                  </div>
                </form>
              </section>
              <section className="bsw-card">
                <div className="bsw-card-heading">
                  <h2>员工列表</h2>
                  <span>{data.users.length} 位员工</span>
                </div>
                <div className="bsw-filter">
                  <input
                    aria-label="查找员工"
                    placeholder="员工姓名或登录账号"
                    value={employeeSearch}
                    onChange={(event) => setEmployeeSearch(event.target.value)}
                  />
                  <select
                    aria-label="按组织筛选员工"
                    value={departmentFilter}
                    onChange={(event) => setDepartmentFilter(event.target.value)}
                  >
                    {organizationOptions('全部组织')}
                  </select>
                </div>
                <div className="bsw-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>员工 / 登录账号</th>
                        <th>所属组织</th>
                        <th>岗位角色</th>
                        <th>登录开通</th>
                        <th>状态</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.users
                        .filter(
                          (user) =>
                            (!departmentFilter || user.organizationId === departmentFilter) &&
                            (!employeeSearch ||
                              `${user.displayName} ${user.username}`.includes(employeeSearch)),
                        )
                        .map((user) => (
                          <tr key={user.id}>
                            <td>
                              <strong>{user.displayName}</strong>
                              <small>{user.username || '登录账号待补充'}</small>
                            </td>
                            <td>
                              {user.organizationId
                                ? (organizationNames.get(user.organizationId) ?? '已配置组织')
                                : '未分配组织'}
                            </td>
                            <td>
                              {user.roleIds
                                .map(
                                  (id) =>
                                    data.roles.find((role) => role.id === id)?.name ?? '已配置角色',
                                )
                                .join('、') || '尚未分配角色'}
                            </td>
                            <td>{badge(user.credentialReady, '已开通', '待开通')}</td>
                            <td>{badge(user.status === 'ACTIVE')}</td>
                            <td className="bsw-row-actions">
                              {!user.credentialReady &&
                                user.status === 'ACTIVE' &&
                                user.id !== data.currentUser.id && (
                                  <button
                                    type="button"
                                    disabled={managementBlocked}
                                    onClick={() =>
                                      navigate(() => {
                                        dismissPasswordReset();
                                        dismissEnrollment();
                                        setEnrollingUser(user);
                                      })
                                    }
                                  >
                                    开通登录
                                  </button>
                                )}
                              {user.credentialReady &&
                                user.status === 'ACTIVE' &&
                                user.id !== data.currentUser.id && (
                                  <button
                                    type="button"
                                    disabled={managementBlocked}
                                    onClick={() =>
                                      navigate(() => {
                                        dismissEnrollment();
                                        dismissPasswordReset();
                                        setResettingUser(user);
                                      })
                                    }
                                  >
                                    重置密码
                                  </button>
                                )}
                              <button
                                type="button"
                                disabled={blocked || user.id === data.currentUser.id}
                                title={
                                  user.id === data.currentUser.id
                                    ? '不能自行变更自己的岗位授权'
                                    : undefined
                                }
                                onClick={() =>
                                  navigate(() => {
                                    setEditingUser(user);
                                    setEmployeeRoles(user.roleIds.slice());
                                  })
                                }
                              >
                                分配角色
                              </button>
                              <button
                                type="button"
                                disabled={blocked || user.id === data.currentUser.id}
                                title={
                                  user.id === data.currentUser.id
                                    ? '不能自行变更自己的组织归属'
                                    : undefined
                                }
                                onClick={() =>
                                  navigate(() => {
                                    setMovingUser(user);
                                    setMovingOrgId(user.organizationId ?? '');
                                    setMovePreview(undefined);
                                  })
                                }
                              >
                                变更组织
                              </button>
                              <button
                                type="button"
                                disabled={blocked || user.id === data.currentUser.id}
                                onClick={() =>
                                  setConfirmation({
                                    title:
                                      user.status === 'ACTIVE'
                                        ? '停用此员工？'
                                        : '重新启用此员工？',
                                    description:
                                      user.status === 'ACTIVE'
                                        ? '停用后现有登录会话失效；历史订单与审计记录保留。'
                                        : '启用仅恢复账号状态，不会新增角色或扩大数据范围。',
                                    action: () => {
                                      setConfirmation(undefined);
                                      void command(
                                        '员工状态',
                                        `${base}/users/${encodeURIComponent(user.id)}/${user.status === 'ACTIVE' ? 'disable' : 'enable'}`,
                                        'POST',
                                        user.status === 'ACTIVE'
                                          ? {}
                                          : { expectedVersion: user.version },
                                      );
                                    },
                                  })
                                }
                              >
                                {user.status === 'ACTIVE' ? '停用' : '启用'}
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {!data.users.length && (
                  <p className="bsw-empty">暂无员工账号，先新增员工再分配角色。</p>
                )}
              </section>
            </>
          )}
          {tab === 'roles' && (
            <>
              <section className="bsw-card">
                <h2>新增岗位角色</h2>
                <div className="bsw-form">
                  <label>
                    角色名称
                    <input
                      aria-label="新角色名称"
                      placeholder="例如：城市运营、订单客服"
                      value={newRoleName}
                      disabled={managementBlocked}
                      onChange={(event) => setNewRoleName(event.target.value)}
                      maxLength={150}
                    />
                  </label>
                  <label>
                    职责说明
                    <input
                      aria-label="新角色职责说明"
                      value={newRoleDescription}
                      disabled={managementBlocked}
                      onChange={(event) => setNewRoleDescription(event.target.value)}
                      maxLength={1000}
                    />
                  </label>
                </div>
                <div className="bsw-inline-actions">
                  <button
                    type="button"
                    className="bsw-primary"
                    disabled={managementBlocked || !newRoleName.trim()}
                    onClick={() =>
                      void command(
                        '岗位角色',
                        `${workspaceBase}/roles`,
                        'POST',
                        {
                          name: newRoleName.trim(),
                          ...(newRoleDescription.trim()
                            ? { description: newRoleDescription.trim() }
                            : {}),
                        },
                        (receipt) => {
                          setNewRoleName('');
                          setNewRoleDescription('');
                          const id = securityText(securityRecord(receipt), 'id');
                          if (id && dataRef.current) adoptRole(dataRef.current, id);
                        },
                      )
                    }
                  >
                    创建岗位角色
                  </button>
                  <span>角色编码由系统生成；新角色默认不授予功能或数据。</span>
                </div>
              </section>
              {roleDraft && (
                <section className="bsw-card">
                  <h2>岗位资料</h2>
                  <div className="bsw-form">
                    <label>
                      角色名称
                      <input
                        aria-label="角色名称"
                        value={roleDraft.name}
                        disabled={blocked}
                        onChange={(event) =>
                          changeRole((role) => ({ ...role, name: event.target.value }))
                        }
                      />
                    </label>
                    <label>
                      职责说明
                      <textarea
                        aria-label="角色职责说明"
                        value={roleDraft.description}
                        disabled={blocked}
                        onChange={(event) =>
                          changeRole((role) => ({ ...role, description: event.target.value }))
                        }
                      />
                    </label>
                  </div>
                  <div className="bsw-summary">
                    <span>
                      已允许{' '}
                      {roleDraft.permissions.filter((rule) => rule.effect === 'ALLOW').length}{' '}
                      项操作
                    </span>
                    <span>
                      已明确禁止{' '}
                      {roleDraft.permissions.filter((rule) => rule.effect === 'DENY').length} 项操作
                    </span>
                    <span>
                      {roleDraft.businessScopeGroups === null
                        ? '沿用历史数据范围'
                        : roleDraft.businessScopeGroups.length
                          ? `已配置 ${roleDraft.businessScopeGroups.length} 组小程序范围`
                          : '尚未授予业务数据'}
                    </span>
                  </div>
                  <div className="bsw-inline-actions">
                    <a href="#role-permission-editor">设置功能与管理范围</a>
                  </div>
                </section>
              )}
              {roleDraft && (
                <section className="bsw-card" id="role-permission-editor">
                  <PermissionScopeEditor
                    key={roleDraft.id}
                    api={api}
                    data={data}
                    permissions={roleDraft.permissions}
                    groups={roleDraft.businessScopeGroups}
                    disabled={blocked}
                    onChange={(permissions, businessScopeGroups, completeCommonReadFields) => {
                      setFieldDraft((fields) =>
                        commonFieldsForGrantedFunctions(
                          fields,
                          completeCommonReadFields ? [] : roleDraft.permissions,
                          permissions,
                          data.permissions,
                        ),
                      );
                      changeRole((role) => ({ ...role, permissions, businessScopeGroups }));
                    }}
                  />
                </section>
              )}
            </>
          )}
          {tab === 'menus' && (
            <section className="bsw-card">
              <div className="bsw-card-heading">
                <div>
                  <h2>这个岗位可以做什么</h2>
                  <p>
                    按后台实际业务模块选择操作，重要操作须单独明确勾选。勾选维护操作时会一并保留所需查看权限；常规全选不会解除已有明确禁止。
                  </p>
                </div>
                <input
                  aria-label="查找业务操作"
                  placeholder="搜索操作名称"
                  value={permissionSearch}
                  onChange={(event) => setPermissionSearch(event.target.value)}
                />
              </div>
              {roleDraft && (
                <details className="bsw-menu-preview">
                  <summary>查看该岗位可打开的后台菜单</summary>
                  <p>
                    对应左侧真实菜单。共享同一查看权限的子页面会一起开放；按钮执行仍由下方操作授权和数据范围共同决定。
                  </p>
                  <div>
                    {businessMenuPreview(roleDraft.permissions, permissionRows).map((module) => (
                      <section key={module.name}>
                        <h3>{module.name}</h3>
                        {module.entries.map((entry) => (
                          <span
                            key={entry.title}
                            className={entry.visible ? 'is-visible' : 'is-hidden'}
                          >
                            {entry.visible ? '✓ ' : '— '}
                            {entry.title}
                          </span>
                        ))}
                      </section>
                    ))}
                  </div>
                </details>
              )}
              {roleDraft ? (
                <div className="bsw-permission-tree">
                  {BUSINESS_PERMISSION_GROUPS.map((group) => {
                    const permissions = visiblePermissions.filter(
                      (permission, index) =>
                        presentPermission(permission, index).groupId === group.id,
                    );
                    if (!permissions.length) return null;
                    const ordinary = permissions.filter(
                      (permission) =>
                        permission.status === 'ACTIVE' &&
                        !permission.highRisk &&
                        presentPermission(permission).action !== 'sensitive',
                    );
                    const groupSelected =
                      ordinary.filter(
                        (permission) => selectedEffect(roleDraft, permission.id) !== 'DENY',
                      ).length > 0 &&
                      ordinary
                        .filter((permission) => selectedEffect(roleDraft, permission.id) !== 'DENY')
                        .every(
                          (permission) => selectedEffect(roleDraft, permission.id) === 'ALLOW',
                        );
                    return (
                      <section key={group.id} className="bsw-permission-group">
                        <header>
                          <h3>{group.name}</h3>
                          {ordinary.length > 0 && (
                            <label>
                              <input
                                type="checkbox"
                                aria-label={`${group.name}常规操作全选`}
                                checked={groupSelected}
                                disabled={blocked}
                                onChange={(event) =>
                                  changeRole((role) => ({
                                    ...role,
                                    permissions: updateBusinessGroup(
                                      role.permissions,
                                      permissionRows,
                                      ordinary,
                                      event.target.checked,
                                    ),
                                  }))
                                }
                              />
                              常规操作全选
                            </label>
                          )}
                        </header>
                        <div>
                          {permissions.map((permission, index) => {
                            const presentation = presentPermission(permission, index),
                              effect = selectedEffect(roleDraft, permission.id);
                            return (
                              <label className="bsw-permission" key={permission.id}>
                                <input
                                  type="checkbox"
                                  aria-label={presentation.title}
                                  checked={effect === 'ALLOW'}
                                  disabled={blocked || permission.status !== 'ACTIVE'}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    const apply = () =>
                                      changeRole((role) => ({
                                        ...role,
                                        permissions: updateBusinessPermission(
                                          role.permissions,
                                          permissionRows,
                                          permission.id,
                                          checked,
                                        ),
                                      }));
                                    if (
                                      checked &&
                                      (effect === 'DENY' ||
                                        permission.highRisk ||
                                        presentation.action === 'sensitive')
                                    )
                                      setConfirmation({
                                        title: '确认授权此操作？',
                                        description: `“${presentation.title}”将允许该角色执行，仍受所属组织与业务范围限制。${effect === 'DENY' ? '此操作会解除该项原有明确禁止。' : ''}`,
                                        action: () => {
                                          apply();
                                          setConfirmation(undefined);
                                        },
                                      });
                                    else apply();
                                  }}
                                />
                                <span>
                                  {presentation.title}
                                  <small>
                                    {permissionActionName(presentation.action)}
                                    {effect === 'DENY'
                                      ? ' · 已明确禁止'
                                      : permission.status !== 'ACTIVE'
                                        ? ' · 已停用'
                                        : ''}
                                  </small>
                                </span>
                                {(permission.highRisk || presentation.action === 'sensitive') && (
                                  <b className="bsw-important">重要</b>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              ) : (
                <p className="bsw-empty">暂无岗位角色，先在岗位角色页新增。</p>
              )}
              {saveBar(roleChanged, () => void saveRole())}
            </section>
          )}
          {tab === 'scope' && (
            <section className="bsw-card">
              <h2>这个岗位可以管理哪些业务</h2>
              <p>每张卡片绑定一个小程序及其城市。只勾选城市，不会获得其他小程序同城数据。</p>
              <Notice tone="warning">
                小程序和城市一起生效，并受所属部门限制。新岗位未分配组织时，暂不能查看部门订单和客户。全量客户画像、收款商户和结算汇总仅平台可用；整小程序配置须授权其全部城市。
              </Notice>
              {roleDraft?.businessScopeGroups === null && (
                <div className="bsw-legacy">
                  <strong>当前角色沿用原有数据范围</strong>
                  <p>
                    原有设置保持生效，不会自动改成全部授权。切换为成组范围后，未选择的小程序与城市将不可访问。
                  </p>
                  <button
                    type="button"
                    disabled={blocked}
                    onClick={() =>
                      setConfirmation({
                        title: '改用小程序与城市成组范围？',
                        description:
                          '此角色将从空范围开始选择；保存后以选择的小程序及城市组合限制数据，未选择组合不再开放。现有功能与未编辑的拒绝规则保留。',
                        action: () => {
                          changeRole((role) => ({ ...role, businessScopeGroups: [] }));
                          setConfirmation(undefined);
                        },
                      })
                    }
                  >
                    配置成组业务范围
                  </button>
                </div>
              )}
              {roleDraft?.businessScopeGroups !== null && roleDraft && (
                <div className="bsw-scope-grid">
                  {data.apps
                    .filter((app) => app.status === 'ACTIVE')
                    .map((app) => {
                      const selection = roleDraft.businessScopeGroups?.find(
                        (group) => group.appId === app.id,
                      );
                      const cities = data.cities.filter(
                        (city) =>
                          city.status === 'ACTIVE' &&
                          (!app.cityIds || app.cityIds.includes(city.id)),
                      );
                      return (
                        <article key={app.id} className="bsw-scope-card">
                          <h3>{app.name}</h3>
                          <span>
                            {selection?.cityIds === null
                              ? '原有全部已开放城市授权'
                              : `${selection?.cityIds.length ?? 0} 个城市已选择`}
                          </span>
                          {selection?.cityIds === null && (
                            <p className="bsw-inline-note">修改城市选择会改成明确的城市清单。</p>
                          )}
                          <div>
                            {cities.map((city) => (
                              <label key={city.id}>
                                <input
                                  type="checkbox"
                                  aria-label={`${app.name} / ${city.name}`}
                                  disabled={blocked}
                                  checked={Boolean(
                                    selection &&
                                    (selection.cityIds === null ||
                                      selection.cityIds.includes(city.id)),
                                  )}
                                  onChange={(event) =>
                                    changeRole((role) => ({
                                      ...role,
                                      businessScopeGroups: updateScopeCity(
                                        (role.businessScopeGroups ?? []).map((group) =>
                                          group.appId === app.id && group.cityIds === null
                                            ? { ...group, cityIds: cities.map((item) => item.id) }
                                            : group,
                                        ),
                                        app.id,
                                        city.id,
                                        event.target.checked,
                                      ),
                                    }))
                                  }
                                />
                                {city.name}
                              </label>
                            ))}
                          </div>
                          {!cities.length && <p>暂无已开放城市可选择。</p>}
                        </article>
                      );
                    })}
                </div>
              )}
              {roleDraft?.businessScopeGroups?.length === 0 && (
                <p className="bsw-inline-note">当前尚未选择业务数据。选择组合并保存后才会授权。</p>
              )}
              {saveBar(roleChanged, () => void saveRole())}
            </section>
          )}
          {(tab === 'fields' || tab === 'roles') && (
            <details className="bsw-card" open={tab === 'fields'}>
              <summary>隐私信息显示设置</summary>
              <h2>这个岗位可以看到哪些敏感信息</h2>
              <p>
                选择查看功能会补齐常用联系与交易金额显示；原有脱敏和禁止设置保留。成本、证件与支付密钥不会自动开放。
              </p>
              <div className="bsw-policy-list">
                {fieldDraft.map((rule, index) => (
                  <div
                    className="bsw-policy-row"
                    key={`${rule.permissionId}:${rule.fieldName}:${index}`}
                  >
                    <div>
                      <strong>{fieldLabel(rule.fieldName)}</strong>
                      <small>
                        {permissionRows.find((permission) => permission.id === rule.permissionId)
                          ? presentPermission(
                              permissionRows.find(
                                (permission) => permission.id === rule.permissionId,
                              )!,
                            ).title
                          : '原有业务授权'}
                      </small>
                    </div>
                    <select
                      aria-label={`${fieldLabel(rule.fieldName)}显示方式`}
                      value={rule.access}
                      disabled={blocked || !roleDraft}
                      onChange={(event) =>
                        setFieldDraft((current) =>
                          replaceFieldRule(current, {
                            ...rule,
                            access: event.target.value as FieldRule['access'],
                          }),
                        )
                      }
                    >
                      <option value="ALLOW" disabled={rule.classification === 'L3'}>
                        可见原文
                      </option>
                      <option value="MASK">脱敏展示</option>
                      <option value="DENY">不可查看</option>
                    </select>
                  </div>
                ))}
              </div>
              {!fieldDraft.length && (
                <p className="bsw-empty">当前角色暂无单独敏感信息规则，可在下方新增。</p>
              )}
              <FieldPolicyCreate
                permissions={permissionRows}
                disabled={blocked || !roleDraft}
                onCreate={(rule) => setFieldDraft((current) => replaceFieldRule(current, rule))}
              />
              {tab === 'fields' &&
                saveBar(fieldsChanged, () => {
                  if (roleDraft)
                    void command(
                      '敏感信息设置',
                      `${base}/roles/${encodeURIComponent(roleDraft.id)}/field-policies`,
                      'PUT',
                      { rules: fieldDraft },
                    );
                })}
            </details>
          )}
          {(tab === 'sensitive' || tab === 'roles') && (
            <details className="bsw-card" open={tab === 'sensitive'}>
              <summary>重要操作的业务阶段限制</summary>
              <h2>重要操作在什么阶段可以执行</h2>
              <p>
                退款、结算、发布等操作还需拥有对应功能权限与业务范围。新增状态规则不会自动授予功能。
              </p>
              <div className="bsw-policy-list">
                {stateDraft.map((rule, index) => (
                  <div
                    className="bsw-policy-row"
                    key={`${rule.permissionId}:${rule.state}:${index}`}
                  >
                    <div>
                      <strong>
                        {permissionRows.find((permission) => permission.id === rule.permissionId)
                          ? presentPermission(
                              permissionRows.find(
                                (permission) => permission.id === rule.permissionId,
                              )!,
                            ).title
                          : '原有重要操作'}
                      </strong>
                      <small>{stateLabel(rule.state)}</small>
                    </div>
                    <select
                      aria-label={`${permissionLabel(rule.permissionId)} / ${stateLabel(rule.state)}操作设置`}
                      value={rule.effect}
                      disabled={blocked || !roleDraft}
                      onChange={(event) =>
                        setStateDraft((current) =>
                          replaceStateRule(current, {
                            ...rule,
                            effect: event.target.value as StateRule['effect'],
                          }),
                        )
                      }
                    >
                      <option value="ALLOW">此阶段允许</option>
                      <option value="DENY">此阶段禁止</option>
                    </select>
                  </div>
                ))}
              </div>
              {!stateDraft.length && <p className="bsw-empty">当前暂无重要操作阶段规则。</p>}
              <StatePolicyCreate
                permissions={permissionRows}
                disabled={blocked || !roleDraft}
                onCreate={(rule) => setStateDraft((current) => replaceStateRule(current, rule))}
              />
              {tab === 'sensitive' &&
                saveBar(statesChanged, () => {
                  if (roleDraft)
                    void command(
                      '重要操作阶段',
                      `${base}/roles/${encodeURIComponent(roleDraft.id)}/state-policies`,
                      'PUT',
                      { rules: stateDraft },
                    );
                })}
            </details>
          )}
          {tab === 'roles' &&
            saveBar(roleChanged || fieldsChanged || statesChanged, () => void saveRole())}
          {tab === 'organizations' && (
            <>
              <div className="bsw-org-layout">
                <section className="bsw-card">
                  <h2>组织层级</h2>
                  <p>组织用于明确员工和订单归属，新建组织不会自动获得业务权限。</p>
                  <div className="bsw-organization-tree">
                    {tree.map(({ organization, depth, cycle, orphan }) => (
                      <article key={organization.id} style={{ marginLeft: `${depth * 18}px` }}>
                        <span className="bsw-tree-dot" />
                        <div>
                          <strong>{organization.name}</strong>
                          <small>
                            {orphan
                              ? '上级组织缺失，需修正归属'
                              : cycle
                                ? '层级关系待修正'
                                : organization.parentId
                                  ? '下级组织'
                                  : '平台直属组织'}
                          </small>
                        </div>
                        {badge(
                          organizationEffectiveActive(data.organizations, organization.id),
                          '启用',
                          organization.status === 'ACTIVE' ? '上级停用/归属异常，暂不可用' : '停用',
                        )}
                        <button
                          type="button"
                          disabled={blocked}
                          aria-label={`修改${organization.name}`}
                          onClick={() =>
                            navigate(() => {
                              setEditingOrganization(organization);
                              setOrganizationDraft({ ...organization });
                            })
                          }
                        >
                          修改
                        </button>
                      </article>
                    ))}
                  </div>
                  {!tree.length && (
                    <p className="bsw-empty">
                      暂无组织。历史订单保留在中央池，未归属员工不自动分配部门。
                    </p>
                  )}
                </section>
                <section className="bsw-card">
                  <h2>新增组织</h2>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!newOrganizationName.trim()) return;
                      void command(
                        '组织',
                        `${workspaceBase}/organizations`,
                        'POST',
                        {
                          name: newOrganizationName.trim(),
                          ...(newOrganizationParent ? { parentId: newOrganizationParent } : {}),
                        },
                        () => {
                          setNewOrganizationName('');
                          setNewOrganizationParent('');
                        },
                      );
                    }}
                  >
                    <div className="bsw-form is-one">
                      <label>
                        组织名称
                        <input
                          aria-label="组织名称"
                          value={newOrganizationName}
                          disabled={blocked}
                          onChange={(event) => setNewOrganizationName(event.target.value)}
                          maxLength={150}
                        />
                      </label>
                      <label>
                        上级组织
                        <select
                          aria-label="上级组织"
                          value={newOrganizationParent}
                          disabled={blocked}
                          onChange={(event) => setNewOrganizationParent(event.target.value)}
                        >
                          {organizationOptions('平台直属组织')}
                        </select>
                      </label>
                    </div>
                    <div className="bsw-inline-actions">
                      <button
                        type="submit"
                        className="bsw-primary"
                        disabled={blocked || !newOrganizationName.trim()}
                      >
                        创建组织
                      </button>
                    </div>
                  </form>
                  <p className="bsw-inline-note">
                    机构之间的管理边界由服务端校验。仅建立层级不会转移历史订单或扩大权限。
                  </p>
                </section>
              </div>
              <section className="bsw-card">
                <div className="bsw-card-heading">
                  <div>
                    <h2>历史订单中央池</h2>
                    <p>仅展示尚未确认组织归属的订单摘要，选择归属后单独确认，不会批量误分配。</p>
                  </div>
                  <span>最多展示 50 条</span>
                </div>
                <div className="bsw-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>订单</th>
                        <th>线路</th>
                        <th>当前归属</th>
                        <th>分配至组织</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.centralOrders.map((order) => {
                        const id = securityText(order, 'id');
                        return (
                          <tr key={id}>
                            <td>{securityText(order, 'orderNo', '历史订单')}</td>
                            <td>
                              {securityText(order, 'productName') ||
                                securityText(order, 'productTitle') ||
                                securityText(securityRecord(order.product), 'title', '已有线路')}
                            </td>
                            <td>平台中央池</td>
                            <td>
                              <select
                                aria-label="订单归属组织"
                                value={orderOrganizations[id] ?? ''}
                                disabled={blocked}
                                onChange={(event) =>
                                  setOrderOrganizations((current) => ({
                                    ...current,
                                    [id]: event.target.value,
                                  }))
                                }
                              >
                                {organizationOptions()}
                              </select>
                            </td>
                            <td>
                              <button
                                type="button"
                                disabled={blocked || !orderOrganizations[id]}
                                onClick={() =>
                                  setConfirmation({
                                    title: '确认此订单的组织归属？',
                                    description: `订单将归属“${organizationNames.get(orderOrganizations[id] ?? '') ?? '所选组织'}”，并按该组织及服务端授权限制查看与处理。不会更改订单金额或支付事实。`,
                                    action: () => {
                                      setConfirmation(undefined);
                                      void command(
                                        '订单组织归属',
                                        `${workspaceBase}/orders/${encodeURIComponent(id)}/organization`,
                                        'PUT',
                                        {
                                          organizationId: orderOrganizations[id],
                                          expectedVersion: Number(
                                            order.version ?? order.statusVersion ?? 0,
                                          ),
                                        },
                                      );
                                    },
                                  })
                                }
                              >
                                确认分配
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {!data.centralOrders.length && (
                  <p className="bsw-empty">当前没有待分配的历史订单。</p>
                )}
              </section>
            </>
          )}
        </>
      )}
      {resettingUser && data && (
        <div className="bsw-drawer-backdrop">
          <section
            className="bsw-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="重置员工登录密码"
          >
            <span className="bsw-password-eyebrow">登录安全 · 管理员操作</span>
            <h2>{resettingUser.displayName} · 重置密码</h2>
            <div className="bsw-password-warning">
              仅重置该员工已开通的密码登录，不改变岗位与数据范围。完成后全部旧会话失效；不能用此入口修改自己的密码。
            </div>
            <p>登录账号：{resettingUser.username}</p>
            <form
              autoComplete="off"
              onSubmit={(event) => {
                event.preventDefault();
                askPasswordReset();
              }}
            >
              <div className="bsw-form is-one">
                <label>
                  新登录密码
                  <input
                    type="password"
                    aria-label="重置员工新密码"
                    autoComplete="new-password"
                    value={resetPassword}
                    maxLength={128}
                    disabled={managementBlocked}
                    onChange={(event) => {
                      setResetPassword(event.target.value);
                      setResetError('');
                    }}
                  />
                </label>
                <label>
                  确认新密码
                  <input
                    type="password"
                    aria-label="再次确认重置密码"
                    autoComplete="new-password"
                    value={resetConfirmation}
                    maxLength={128}
                    disabled={managementBlocked}
                    onChange={(event) => {
                      setResetConfirmation(event.target.value);
                      setResetError('');
                    }}
                  />
                </label>
              </div>
              <p>
                12–128
                个字符，包含英文大写、小写和数字，可使用符号。系统不会再次显示或保存到页面设置中。
              </p>
              {resetError && <Notice tone="error">{resetError}</Notice>}
              <footer>
                <button type="button" disabled={busy} onClick={dismissPasswordReset}>
                  取消
                </button>
                <ActionButton
                  type="submit"
                  className="bsw-primary"
                  pending={busy}
                  disabled={managementBlocked}
                >
                  确认重置密码
                </ActionButton>
              </footer>
            </form>
          </section>
        </div>
      )}
      {enrollingUser && data && (
        <div className="bsw-drawer-backdrop">
          <section
            className="bsw-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="初次开通员工登录"
          >
            <h2>{enrollingUser.displayName} · 初次开通登录</h2>
            <p>
              开通仅启用密码登录，不新增业务权限。此入口只用于初次开通，不能重置已开通账号的密码。
            </p>
            <p>登录账号：{enrollingUser.username}</p>
            <form
              autoComplete="off"
              onSubmit={(event) => {
                event.preventDefault();
                void enrollLogin();
              }}
            >
              <div className="bsw-form is-one">
                <label>
                  初始密码
                  <input
                    type="password"
                    aria-label="初始登录密码"
                    autoComplete="new-password"
                    value={initialPassword}
                    maxLength={128}
                    disabled={managementBlocked}
                    onChange={(event) => {
                      setInitialPassword(event.target.value);
                      setEnrollmentError('');
                    }}
                  />
                </label>
                <label>
                  确认密码
                  <input
                    type="password"
                    aria-label="确认初始登录密码"
                    autoComplete="new-password"
                    value={confirmedPassword}
                    maxLength={128}
                    disabled={managementBlocked}
                    onChange={(event) => {
                      setConfirmedPassword(event.target.value);
                      setEnrollmentError('');
                    }}
                  />
                </label>
              </div>
              <p>
                使用 12–128
                个字符，需同时包含英文大写、小写和数字，可使用符号。请通过安全渠道告知该员工；系统不会再次显示密码。
              </p>
              {enrollmentError && <Notice tone="error">{enrollmentError}</Notice>}
              <footer>
                <button type="button" disabled={busy} onClick={dismissEnrollment}>
                  取消
                </button>
                <ActionButton
                  type="submit"
                  className="bsw-primary"
                  pending={busy}
                  disabled={managementBlocked}
                >
                  确认开通登录
                </ActionButton>
              </footer>
            </form>
          </section>
        </div>
      )}
      {editingOrganization && organizationDraft && data && (
        <div className="bsw-drawer-backdrop">
          <section className="bsw-drawer" role="dialog" aria-modal="true" aria-label="修改组织">
            <h2>修改组织 · {editingOrganization.name}</h2>
            <p>
              调整上级会影响该组织及下级员工的管理边界；停用不会删除历史人员与订单，也不会转移归属。
            </p>
            <div className="bsw-form is-one">
              <label>
                组织名称
                <input
                  aria-label="修改组织名称"
                  value={organizationDraft.name}
                  disabled={managementBlocked}
                  maxLength={150}
                  onChange={(event) =>
                    setOrganizationDraft({ ...organizationDraft, name: event.target.value })
                  }
                />
              </label>
              <label>
                上级组织
                <select
                  aria-label="修改上级组织"
                  value={organizationDraft.parentId ?? ''}
                  disabled={managementBlocked}
                  onChange={(event) =>
                    setOrganizationDraft({
                      ...organizationDraft,
                      parentId: event.target.value || null,
                    })
                  }
                >
                  <option value="">平台直属</option>
                  {invalidDraftParent && (
                    <option value={organizationDraft.parentId ?? ''} disabled>
                      当前上级不可用，请重新选择
                    </option>
                  )}
                  {availableParents.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                组织状态
                <select
                  aria-label="修改组织状态"
                  value={organizationDraft.status}
                  disabled={managementBlocked}
                  onChange={(event) =>
                    setOrganizationDraft({ ...organizationDraft, status: event.target.value })
                  }
                >
                  <option value="ACTIVE">启用</option>
                  <option value="DISABLED">停用</option>
                </select>
              </label>
            </div>
            <footer>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setEditingOrganization(undefined);
                  setOrganizationDraft(undefined);
                }}
              >
                取消
              </button>
              <button
                type="button"
                className="bsw-primary"
                disabled={
                  managementBlocked ||
                  invalidDraftParent ||
                  !organizationDraft.name.trim() ||
                  JSON.stringify(organizationDraft) === JSON.stringify(editingOrganization)
                }
                onClick={() =>
                  setConfirmation({
                    title: '确认修改组织？',
                    description:
                      '组织层级和状态会影响员工的管理边界。请确认新归属正确，已产生的业务与账目将保留。',
                    action: () => {
                      setConfirmation(undefined);
                      void command(
                        '组织资料',
                        `${workspaceBase}/organizations/${encodeURIComponent(editingOrganization.id)}`,
                        'PUT',
                        {
                          expectedVersion: editingOrganization.version,
                          name: organizationDraft.name.trim(),
                          parentId: organizationDraft.parentId,
                          status: organizationDraft.status,
                        },
                        () => {
                          setEditingOrganization(undefined);
                          setOrganizationDraft(undefined);
                        },
                      );
                    },
                  })
                }
              >
                保存组织修改
              </button>
            </footer>
          </section>
        </div>
      )}
      {editingUser && data && (
        <div className="bsw-drawer-backdrop">
          <section className="bsw-drawer" role="dialog" aria-modal="true" aria-label="分配员工角色">
            <h2>{editingUser.displayName} · 岗位角色</h2>
            <p>保存后该员工现有登录会话将失效，重新登录后使用新授权。</p>
            <div className="bsw-choice-list">
              {data.roles.map((role) => (
                <label key={role.id}>
                  <input
                    type="checkbox"
                    disabled={blocked}
                    checked={employeeRoles.includes(role.id)}
                    onChange={(event) =>
                      setEmployeeRoles((current) =>
                        event.target.checked
                          ? [...new Set([...current, role.id])]
                          : current.filter((id) => id !== role.id),
                      )
                    }
                  />
                  {role.name}
                </label>
              ))}
            </div>
            <footer>
              <button type="button" disabled={busy} onClick={() => setEditingUser(undefined)}>
                取消
              </button>
              <button
                type="button"
                className="bsw-primary"
                disabled={blocked}
                onClick={() =>
                  void command(
                    '员工岗位角色',
                    `${base}/users/${encodeURIComponent(editingUser.id)}/roles`,
                    'PUT',
                    { roleIds: employeeRoles },
                    () => setEditingUser(undefined),
                  )
                }
              >
                保存员工角色
              </button>
            </footer>
          </section>
        </div>
      )}
      {movingUser && data && (
        <div className="bsw-drawer-backdrop">
          <section className="bsw-drawer" role="dialog" aria-modal="true" aria-label="变更员工组织">
            <h2>{movingUser.displayName} · 变更组织</h2>
            <p>先检查影响再确认变更。所属组织会影响管理边界，不会自动授予更多功能或小程序数据。</p>
            <label>
              新的所属组织
              <select
                aria-label="新的所属组织"
                value={movingOrgId}
                disabled={blocked || previewBusy}
                onChange={(event) => {
                  setMovingOrgId(event.target.value);
                  setMovePreview(undefined);
                }}
              >
                {organizationOptions('暂不分配组织')}
              </select>
            </label>
            {movePreview && (
              <Notice tone="warning">
                影响检查已完成。变更后需重新登录；原岗位角色保留，数据访问按新的组织归属与已授予业务范围重新判定。
                {Array.isArray(movePreview.visibleOrganizationIds) && (
                  <span>
                    {' '}
                    新的管理组织范围包含 {movePreview.visibleOrganizationIds.length} 个组织。
                  </span>
                )}
                <span> 已产生的订单和账目不会随员工归属迁移。</span>
              </Notice>
            )}
            <footer>
              <button
                type="button"
                disabled={busy || previewBusy}
                onClick={() => {
                  setMovingUser(undefined);
                  setMovePreview(undefined);
                }}
              >
                取消
              </button>
              <button
                type="button"
                disabled={
                  blocked || previewBusy || (movingOrgId || null) === movingUser.organizationId
                }
                onClick={() => {
                  const controller = new AbortController();
                  operation.current = controller;
                  const id = generation.current;
                  setPreviewBusy(true);
                  void boundedRequest<JsonRecord>(
                    api,
                    `${workspaceBase}/users/${encodeURIComponent(movingUser.id)}/organization-preview`,
                    {
                      method: 'POST',
                      body: JSON.stringify({ organizationId: movingOrgId || null }),
                    },
                    controller.signal,
                  )
                    .then((result) => {
                      if (!controller.signal.aborted && generation.current === id)
                        setMovePreview(securityRecord(result));
                    })
                    .catch((error) => {
                      if (!controller.signal.aborted && generation.current === id)
                        setFeedback(errorMessage(error, '组织影响检查失败，请重试。'));
                    })
                    .finally(() => {
                      if (generation.current === id) setPreviewBusy(false);
                      if (operation.current === controller) operation.current = undefined;
                    });
                }}
              >
                {previewBusy ? '检查中…' : '检查变更影响'}
              </button>
              <button
                type="button"
                className="bsw-primary"
                disabled={blocked || !movePreview}
                onClick={() =>
                  setConfirmation({
                    title: '确认变更员工所属组织？',
                    description:
                      '新归属将立即参与权限隔离，该员工现有会话将被注销。请确认组织选择正确。',
                    action: () => {
                      setConfirmation(undefined);
                      void command(
                        '员工组织归属',
                        `${workspaceBase}/users/${encodeURIComponent(movingUser.id)}/organization`,
                        'PUT',
                        {
                          organizationId: movingOrgId || null,
                          expectedVersion: Number(
                            movePreview?.expectedVersion ?? movingUser.version,
                          ),
                          acknowledgedImpact: true,
                        },
                        () => {
                          setMovingUser(undefined);
                          setMovePreview(undefined);
                        },
                      );
                    },
                  })
                }
              >
                确认变更组织
              </button>
            </footer>
          </section>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(pendingNavigation)}
        title="放弃未保存的修改？"
        description="当前尚有未保存的配置。继续切换会恢复为服务端最后一次已确认的设置。"
        confirmLabel="放弃并继续"
        onCancel={() => {
          setPendingNavigation(undefined);
          if (resettingUser) clearResetPasswords();
        }}
        onConfirm={() => {
          const action = pendingNavigation;
          setPendingNavigation(undefined);
          if (data) adoptRole(data, selectedRoleId);
          setNewRoleName('');
          setNewRoleDescription('');
          setNewAccount('');
          setNewEmployeeName('');
          setNewOrganizationName('');
          setNewEmployeeOrg('');
          setNewEmployeeError('');
          setNewOrganizationParent('');
          setEditingOrganization(undefined);
          setOrganizationDraft(undefined);
          setMovePreview(undefined);
          setEditingUser(undefined);
          dismissEnrollment();
          dismissPasswordReset();
          setMovingUser(undefined);
          action?.();
        }}
      />
      <ConfirmDialog
        open={Boolean(confirmation)}
        title={confirmation?.title ?? ''}
        description={confirmation?.description}
        pending={busy}
        onCancel={() => {
          if (!busy) {
            setConfirmation(undefined);
            if (resettingUser) clearResetPasswords();
          }
        }}
        onConfirm={() => confirmation?.action()}
      />
    </section>
  );
}

function FieldPolicyCreate({
  permissions,
  disabled,
  onCreate,
}: {
  permissions: WorkspaceData['permissions'];
  disabled: boolean;
  onCreate: (rule: FieldRule) => void;
}) {
  const [permissionId, setPermissionId] = useState(''),
    [name, setName] = useState(''),
    [access, setAccess] = useState<FieldRule['access']>('MASK');
  const field = FIELD_CHOICES.find((item) => item.name === name);
  return (
    <div className="bsw-add-policy">
      <h3>新增敏感信息规则</h3>
      <div className="bsw-form">
        <label>
          对应业务操作
          <select
            aria-label="敏感信息对应操作"
            value={permissionId}
            disabled={disabled}
            onChange={(event) => setPermissionId(event.target.value)}
          >
            <option value="">请选择业务操作</option>
            {permissions
              .filter((permission) => permission.status === 'ACTIVE')
              .map((permission, index) => (
                <option key={permission.id} value={permission.id}>
                  {presentPermission(permission, index).title}
                </option>
              ))}
          </select>
        </label>
        <label>
          业务信息
          <select
            aria-label="选择敏感信息"
            value={name}
            disabled={disabled}
            onChange={(event) => {
              setName(event.target.value);
              if (
                FIELD_CHOICES.find((item) => item.name === event.target.value)?.classification ===
                'L3'
              )
                setAccess('DENY');
            }}
          >
            <option value="">请选择信息</option>
            {FIELD_CHOICES.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          展示方式
          <select
            aria-label="新增敏感信息展示方式"
            value={access}
            disabled={disabled}
            onChange={(event) => setAccess(event.target.value as FieldRule['access'])}
          >
            <option value="ALLOW" disabled={field?.classification === 'L3'}>
              可见原文
            </option>
            <option value="MASK">脱敏展示</option>
            <option value="DENY">不可查看</option>
          </select>
        </label>
      </div>
      <button
        type="button"
        disabled={disabled || !permissionId || !field}
        onClick={() => {
          if (field) {
            onCreate({
              permissionId,
              fieldName: field.name,
              classification: field.classification,
              access,
            });
            setPermissionId('');
            setName('');
            setAccess('MASK');
          }
        }}
      >
        加入待保存设置
      </button>
    </div>
  );
}
function StatePolicyCreate({
  permissions,
  disabled,
  onCreate,
}: {
  permissions: WorkspaceData['permissions'];
  disabled: boolean;
  onCreate: (rule: StateRule) => void;
}) {
  const [permissionId, setPermissionId] = useState(''),
    [state, setState] = useState(''),
    [effect, setEffect] = useState<StateRule['effect']>('DENY');
  return (
    <div className="bsw-add-policy">
      <h3>新增重要操作阶段</h3>
      <div className="bsw-form">
        <label>
          重要操作
          <select
            aria-label="选择重要操作"
            value={permissionId}
            disabled={disabled}
            onChange={(event) => setPermissionId(event.target.value)}
          >
            <option value="">请选择操作</option>
            {permissions
              .filter((permission) => permission.highRisk && permission.status === 'ACTIVE')
              .map((permission, index) => (
                <option key={permission.id} value={permission.id}>
                  {presentPermission(permission, index).title}
                </option>
              ))}
          </select>
        </label>
        <label>
          业务阶段
          <select
            aria-label="选择业务阶段"
            value={state}
            disabled={disabled}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="">请选择阶段</option>
            {BUSINESS_STATES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          此阶段操作
          <select
            aria-label="新增阶段是否允许"
            value={effect}
            disabled={disabled}
            onChange={(event) => setEffect(event.target.value as StateRule['effect'])}
          >
            <option value="DENY">此阶段禁止</option>
            <option value="ALLOW">此阶段允许</option>
          </select>
        </label>
      </div>
      <button
        type="button"
        disabled={disabled || !permissionId || !state}
        onClick={() => {
          onCreate({ permissionId, state, effect });
          setPermissionId('');
          setState('');
          setEffect('DENY');
        }}
      >
        加入待保存设置
      </button>
    </div>
  );
}

export function BusinessSecurityMyAccessPage({ api }: { api: AdminApiClient }) {
  const [value, setValue] = useState<JsonRecord>(),
    [error, setError] = useState(''),
    [loading, setLoading] = useState(true),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    void boundedRequest<JsonRecord>(api, '/api/admin/m8/security/me', {}, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setValue(securityRecord(next));
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(errorMessage(cause, '个人授权读取失败，请重新加载。'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [api, revision]);
  const codes = Array.isArray(value?.permissionCodes)
    ? value.permissionCodes.filter((code): code is string => typeof code === 'string')
    : [];
  const groups = Array.isArray(value?.businessScopeGroups)
    ? value.businessScopeGroups.map(securityRecord)
    : [];
  const apps = Array.isArray(value?.apps) ? value.apps.map(securityRecord) : [],
    cities = Array.isArray(value?.cities) ? value.cities.map(securityRecord) : [];
  const appNames = new Map(
    apps.map((item) => [
      securityText(item, 'id'),
      securityText(item, 'displayName') || securityText(item, 'name'),
    ]),
  );
  const cityNames = new Map(
    cities.map((item) => [securityText(item, 'id'), securityText(item, 'name')]),
  );
  return (
    <section className="bsw-workspace" aria-label="我的真实权限">
      <header className="bsw-heading">
        <div>
          <h1>我的权限与业务范围</h1>
          <p>以下信息来自当前登录账号的实际授权。</p>
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={() => setRevision((current) => current + 1)}
        >
          重新读取
        </button>
      </header>
      {loading && <Notice>正在读取个人授权…</Notice>}
      {error && <Notice tone="error">{error}</Notice>}
      {value && !error && (
        <>
          <section className="bsw-card">
            <h2>当前账号</h2>
            <div className="bsw-summary">
              <span>
                {value.platformAccess === true ? '平台管理范围' : '按已授权组织与业务范围访问'}
              </span>
              <span>
                {securityText(value, 'organizationName') ||
                  (value.organizationId ? '已设置所属组织' : '平台中央归属')}
              </span>
              <span>{value.isolationEnabled === true ? '组织隔离已生效' : '沿用已有授权规则'}</span>
              {Array.isArray(value.roleNames) && (
                <span>
                  岗位：
                  {value.roleNames
                    .filter((item): item is string => typeof item === 'string')
                    .join('、') || '未分配岗位'}
                </span>
              )}
            </div>
          </section>
          <section className="bsw-card">
            <h2>我可以执行的业务操作</h2>
            <div className="bsw-access-list">
              {codes.map((code, index) => {
                const presented = presentPermission(
                  { id: '', code, description: '', highRisk: false, status: 'ACTIVE' },
                  index,
                );
                return <span key={code}>{presented.title}</span>;
              })}
            </div>
            {!codes.length && <p className="bsw-empty">当前账号尚未获授业务操作。</p>}
          </section>
          <section className="bsw-card">
            <h2>我可以管理的小程序与城市</h2>
            {value.platformAccess === true ? (
              <p>可在平台授权范围内管理业务；重要操作仍需相应授权。</p>
            ) : groups.length ? (
              <div className="bsw-scope-grid">
                {groups.map((group, index) => {
                  const cityIds = Array.isArray(group.cityIds)
                    ? group.cityIds.filter((id): id is string => typeof id === 'string')
                    : [];
                  return (
                    <article
                      key={`${securityText(group, 'appId')}:${index}`}
                      className="bsw-scope-card"
                    >
                      <h3>
                        {appNames.get(securityText(group, 'appId')) || `已授权小程序 ${index + 1}`}
                      </h3>
                      <p>
                        {group.cityIds === null
                          ? '该小程序已授权的全部开放城市'
                          : cityIds
                              .map(
                                (id, cityIndex) =>
                                  cityNames.get(id) || `已授权城市 ${cityIndex + 1}`,
                              )
                              .join('、') || '尚未选择城市'}
                      </p>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="bsw-empty">当前没有可管理的业务组合，请联系管理员配置。</p>
            )}
          </section>
        </>
      )}
    </section>
  );
}
